import React, { useState } from "react";
import { Star } from "lucide-react";
import SchoolDocumentHeader from "@/components/SchoolDocumentHeader";
import { Field, Pill, StatCard, inputCls, btnCls, btnGhost } from "./ui";

export const GRADES = ["A", "B", "C", "D"];
export const GRADE_LABELS = { A: "متميز", B: "جيد جدًا", C: "مقبول", D: "يحتاج متابعة" };
export const GRADE_COLORS = { A: "bg-emerald-100 text-emerald-800", B: "bg-sky-100 text-sky-800", C: "bg-amber-100 text-amber-800", D: "bg-red-100 text-red-800" };
export const ROLE_NAMES = { teacher: "المعلم", deputy: "الوكيل", activity: "رائد النشاط", parent: "ولي الأمر" };
export const STATUS = {
  pending: ["بانتظار اعتماد المشرف الإرشادي", "bg-amber-100 text-amber-800"],
  approved: ["معتمد — جاهز للإرسال", "bg-sky-100 text-sky-800"],
  sent: ["أُرسل لولي الأمر", "bg-emerald-100 text-emerald-800"],
};
export const letterOf = (p) => (p == null ? null : p >= 87.5 ? "A" : p >= 62.5 ? "B" : p >= 37.5 ? "C" : "D");
export const isGirl = (s) => s?.gender === "female" || /أنثى|انثى|بنات|female/i.test(String(s?.gender || ""));
export const linkOf = (token) => `${window.location.origin}/f/${token}`;
export const studentPhone = (s) => s?.guardian_phone || s?.father_phone || s?.mother_phone || "";

export const fmtWeek = (w) => {
  try { return new Date(`${w}T12:00:00`).toLocaleDateString("ar-SA-u-ca-gregory-nu-latn", { day: "numeric", month: "long" }); } catch { return w; }
};
const fmtWeekEn = (w) => {
  try { return new Date(`${w}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long" }); } catch { return w; }
};

export function GradePill({ pct }) {
  const g = letterOf(pct);
  if (!g) return <Pill className="bg-gray-100 text-gray-600">—</Pill>;
  return <Pill className={GRADE_COLORS[g]}>{g} · {pct}%</Pill>;
}

// ---------- نصوص رسائل الواتساب (عربي + إنجليزي) ----------
export function requestMessage(c, r, counselorName) {
  const s = c.student, girl = isGirl(s), M = (m, f) => (girl ? f : m);
  const wkA = fmtWeek(c.week), wkE = fmtWeekEn(c.week);
  const cls = String(s.grade || "").trim();
  const sign = `${M("المرشد الطلابي", "المرشدة الطلابية")} / Student Counselor: ${counselorName}`;
  const link = linkOf(r.token);
  if (r.role === "parent") {
    return `ولي الأمر المحترم\nالسلام عليكم ورحمة الله وبركاته\n${M("ابنكم", "ابنتكم")} ${s.name} ${M("مشمول", "مشمولة")} هذا الأسبوع (${wkA}) ببرنامج المتابعة الإيجابية في المدرسة. نأمل مشاركتكم بتقييم ${M("ابنكم", "ابنتكم")} في المنزل من A إلى D — يوميًا أو مرة في الأسبوع — في: النوم مبكرًا، الاستيقاظ مبكرًا، القيام بأعماله بنفسه، أداء الواجبات، أداء الصلوات الخمس، برّ الوالدين.\nرابط التقييم:\n${link}\n\nDear parent,\nThis week (${wkE}) ${s.name} is part of our positive follow-up program. Please rate your child at home from A to D, daily or once a week: sleeping early, waking up early, doing tasks independently, homework, the five prayers, kindness to parents.\nRating link:\n${link}\n\n${sign}`;
  }
  if (r.role === "deputy") {
    return `${r.name} المحترم\nالسلام عليكم ورحمة الله وبركاته\nنأمل متابعة ${M("الطالب", "الطالبة")} ${s.name} (${cls}) خلال هذا الأسبوع (${wkA})، و${M("تقييمه", "تقييمها")} من A إلى D في الطابور والفسحة والصلاة، وتسجيل أي مخالفة ${M("عليه", "عليها")} من نفس الرابط (لا يحتاج حسابًا، ويمكن التقييم كل يوم):\n${link}\nشاكرين تعاونكم.\n\nDear ${r.name},\nPlease follow up ${s.name} (${cls}) this week (${wkE}) and rate the student A–D for morning line, recess and prayer, and record any violation, using this link (no account needed, daily if you wish):\n${link}\nThank you.\n\n${sign}`;
  }
  const subj = r.role === "teacher" && r.subjects ? ` في حصص ${r.subjects}` : r.role === "activity" ? " في النشاط" : "";
  const subjE = r.role === "teacher" && r.subjects ? ` in your ${r.subjects} classes` : r.role === "activity" ? " in activities" : "";
  return `${r.name} المحترم\nالسلام عليكم ورحمة الله وبركاته\nنأمل منكم متابعة ${M("الطالب", "الطالبة")} ${s.name} (${cls})${subj} خلال هذا الأسبوع (${wkA})، و${M("تقييمه", "تقييمها")} سلوكيًا وتعليميًا من A إلى D بعد كل ${r.role === "activity" ? "نشاط" : "حصة"} عبر هذا الرابط (لا يحتاج حسابًا):\n${link}\nشاكرين تعاونكم.\n\nDear ${r.name},\nPlease follow up ${s.name} (${cls})${subjE} during this week (${wkE}) and rate the student from A to D in behavior and learning after each ${r.role === "activity" ? "activity" : "class"} using this link (no account needed):\n${link}\nThank you.\n\n${sign}`;
}

export function reportMessage(rep, counselorName) {
  const s = rep.student, girl = isGirl(s), M = (m, f) => (girl ? f : m);
  const join = (arr, sep) => arr.map((x) => x.ar).join(sep);
  const e = rep.eligibility || {};
  const lines = [
    "السلام عليكم ورحمة الله وبركاته",
    `ولي أمر ${M("الطالب", "الطالبة")}: ${s.name}`,
    `التقرير الأسبوعي لمتابعة السلوك — أسبوع ${fmtWeek(rep.week)}`,
    `النسبة العامة: ${rep.pct ?? "—"}% (${rep.letter || "—"}) — سلوكي ${rep.pct_b ?? "—"}% — تعليمي ${rep.pct_l ?? "—"}%`,
    rep.strong.length ? `نقاط القوة: ${join(rep.strong, "، ")}` : "",
    rep.weak.length ? `يحتاج إلى دعم: ${join(rep.weak, "، ")}` : "",
    (rep.violations || []).length ? `المخالفات المسجلة هذا الأسبوع: ${rep.violations.length}` : "",
    rep.recs.length ? `التوصيات:\n- ${join(rep.recs, "\n- ")}` : "",
    rep.sup_note ? `ملاحظة المشرف الإرشادي: ${rep.sup_note}` : "",
    e.honor ? `★ ${M("مستحق", "مستحقة")} للتكريم لاستمرار التحسن شهرًا كاملًا` : e.thanks ? `★ ${M("مستحق", "مستحقة")} لشهادة شكر وتقدير` : "",
  ].filter(Boolean).join("\n");
  const link = rep.parent_token ? `\n\nرابط متابعة ${M("ابنكم", "ابنتكم")}:\n${linkOf(rep.parent_token)}` : "";
  return `${lines}${link}\n\n${M("المرشد الطلابي", "المرشدة الطلابية")}: ${counselorName}`;
}

// ---------- استمارة التقييم (للروابط الخاصة) ----------
export function RatingForm({ criteria, initial, onSave, onCancel, withComplaint, saveLabel }) {
  const [scores, setScores] = useState(initial?.scores || {});
  const [note, setNote] = useState(initial?.note || "");
  const [complaint, setComplaint] = useState(!!initial?.complaint);
  const [busy, setBusy] = useState(false);
  const ok = criteria.every((c) => scores[c.key]);
  return (
    <div className="space-y-4" data-testid="fu-rating-form">
      {[["b", "سلوكي"], ["l", "تعليمي"]].map(([g, gl]) => {
        const ks = criteria.filter((c) => c.group === g);
        if (!ks.length) return null;
        return (
          <div key={g} className="space-y-3">
            <div className="text-sm font-bold text-[#8A6D2B]">{gl}</div>
            {ks.map((c) => (
              <div key={c.key}>
                <div className="text-sm mb-1.5">{c.ar}</div>
                <div className="grid grid-cols-4 gap-2">
                  {GRADES.map((gr) => (
                    <button type="button" key={gr} onClick={() => setScores({ ...scores, [c.key]: gr })} data-testid={`fu-${c.key}-${gr}`}
                      className={`rounded-xl border py-2 text-xs leading-5 transition ${scores[c.key] === gr ? "bg-[#3E6A56] text-white border-[#3E6A56]" : "bg-white border-[#e6e2d8] hover:bg-[#F9F8F6]"}`}>
                      <span className="block text-lg font-bold">{gr}</span>{GRADE_LABELS[gr]}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      })}
      {withComplaint && (
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={complaint} onChange={(e) => setComplaint(e.target.checked)} /> توجد شكوى على الطالب</label>
      )}
      <Field label="ملاحظة (اختياري)"><textarea className={inputCls} rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      <div className="flex gap-2">
        <button className={btnCls} disabled={!ok || busy} data-testid="fu-rating-save"
          onClick={async () => { setBusy(true); try { await onSave({ scores, note, complaint }); } finally { setBusy(false); } }}>{saveLabel || "حفظ التقييم"}</button>
        {onCancel && <button className={btnGhost} onClick={onCancel}>إلغاء</button>}
      </div>
    </div>
  );
}

export function Violations({ list, onDelete }) {
  if (!list.length) return <div className="text-sm text-[#4A554E]">لا توجد مخالفات مسجلة.</div>;
  return list.map((v) => (
    <div key={v.id} className="text-sm py-1 border-b border-[#f0ede4] flex items-center gap-2 flex-wrap">
      <span>{v.date} — {v.text}</span>
      {v.degree && <Pill className="bg-red-100 text-red-800">الدرجة {v.degree}</Pill>}
      {onDelete && <button type="button" className="text-xs underline text-red-700 ms-auto" onClick={() => onDelete(v)}>حذف</button>}
    </div>
  ));
}

// ---------- جدول المقيّمين ----------
export function RatersTable({ raters }) {
  return (
    <div className="overflow-x-auto border border-[#e6e2d8] rounded-xl" data-testid="fu-raters">
      <table className="w-full text-sm">
        <thead><tr>
          <th className="p-2 text-start">المقيّم</th><th className="p-2 text-start">الجهة</th>
          <th className="p-2">عدد التقييمات</th><th className="p-2">التقدير</th>
        </tr></thead>
        <tbody>
          {raters.map((r) => (
            <tr key={`${r.role}-${r.id}`} className="border-t border-[#f0ede4] align-top">
              <td className="p-2"><div className="font-medium">{r.name || ROLE_NAMES[r.role]}</div>
                {(r.notes || []).map((n, i) => <div key={i} className="text-xs text-[#4A554E]">“{n}”</div>)}
                {r.complaint && <Pill className="bg-red-100 text-red-800">توجد شكوى</Pill>}</td>
              <td className="p-2 text-[#4A554E]">{ROLE_NAMES[r.role]}{r.subjects ? ` — ${r.subjects}` : ""}</td>
              <td className="p-2 text-center">{r.entries}</td>
              <td className="p-2 text-center">{r.entries ? <GradePill pct={r.pct} /> : <Pill className="bg-gray-100 text-gray-600">لم يقيّم</Pill>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function History({ history }) {
  const rows = history.slice(-6);
  return (
    <div>
      <div className="font-bold text-sm mb-2">المعدل الأسبوعي</div>
      <div className="flex items-end gap-2 h-28 border-b border-[#e6e2d8]">
        {rows.map((h) => (
          <div key={h.id} className="flex-1 flex flex-col items-center justify-end h-full gap-1">
            <span className="text-[11px] text-[#4A554E]">{h.pct ?? "—"}</span>
            <div className={`w-full max-w-[40px] rounded-t-md ${h.status === "active" ? "bg-[#3E6A56]" : "bg-[#3E6A56]/40"}`} style={{ height: `${Math.max(3, (h.pct || 0) * 0.75)}%` }} />
          </div>
        ))}
      </div>
      <div className="flex gap-2">{rows.map((h) => <span key={h.id} className="flex-1 text-center text-[11px] text-[#4A554E]">{fmtWeek(h.week)}</span>)}</div>
    </div>
  );
}

// ---------- التقرير الأسبوعي ----------
export function ReportBody({ rep }) {
  const e = rep.eligibility || {};
  const d = e.delta;
  return (
    <div className="space-y-4" data-testid="fu-report">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="النسبة العامة" value={rep.pct == null ? "—" : `${rep.letter} · ${rep.pct}%`} />
        <StatCard label="سلوكي" value={rep.pct_b == null ? "—" : `${rep.pct_b}%`} />
        <StatCard label="تعليمي" value={rep.pct_l == null ? "—" : `${rep.pct_l}%`} />
        <StatCard label="عن الأسبوع السابق" value={d == null ? "—" : d > 0 ? `▲ ${d}` : d < 0 ? `▼ ${-d}` : "="} tone={d > 0 ? "text-emerald-700" : d < 0 ? "text-red-700" : "text-[#4A554E]"} />
      </div>
      <div>
        <div className="font-bold mb-2">استمارة التقييم</div>
        <RatersTable raters={rep.raters || []} />
      </div>
      <div className="border border-[#e6e2d8] rounded-xl p-4">
        <div className="font-bold mb-2">التقييم حسب البند</div>
        {(rep.items || []).map((i) => (
          <div key={i.key} className="flex items-center gap-2 py-1 text-sm">
            <span className="flex-1">{i.ar}</span>
            <div className="w-24 h-2 bg-[#EEF3F2] rounded-full overflow-hidden"><div className="h-full bg-[#3E6A56]" style={{ width: `${i.pct}%` }} /></div>
            <b className="w-5 text-center">{i.letter}</b>
          </div>
        ))}
      </div>
      <div className="border border-[#e6e2d8] rounded-xl p-4 space-y-2">
        <div className="font-bold">✦ التحليل والتوصيات</div>
        {rep.strong?.length > 0 && <div className="text-sm"><b>نقاط القوة:</b> {rep.strong.map((x) => x.ar).join("، ")}</div>}
        {rep.weak?.length > 0 && <div className="text-sm"><b>يحتاج إلى دعم:</b> {rep.weak.map((x) => x.ar).join("، ")}</div>}
        <ul className="list-disc ps-5 text-sm space-y-1">{(rep.recs || []).map((r, i) => <li key={i}>{r.ar}</li>)}</ul>
        {rep.complaints > 0 && <Pill className="bg-red-100 text-red-800">{rep.complaints} شكوى مسجلة</Pill>}
      </div>
      <div className="border border-[#e6e2d8] rounded-xl p-4">
        <div className="font-bold mb-2">المخالفات المسجلة هذا الأسبوع</div>
        <Violations list={rep.violations || []} />
      </div>
      {rep.history?.length > 1 && <History history={rep.history} />}
      {(e.thanks || e.honor) && <div className="rounded-xl bg-[#B08D3C]/15 text-[#8A6D2B] p-3 font-bold"><Star className="inline w-4 h-4" /> {e.honor ? "مستحق للتكريم: تحسّن مستمر لمدة شهر كامل" : "مستحق لشهادة شكر وتقدير"}</div>}
    </div>
  );
}

export function Certificate({ rep }) {
  const s = rep.student, girl = isGirl(s), honor = rep.eligibility?.honor, sg = rep.signatures || {};
  return (
    <div className="report-print bg-white border-4 border-double border-[#B08D3C] rounded-2xl p-8 text-center" data-testid="fu-cert">
      <SchoolDocumentHeader schoolKey={sg.school_key} gender={sg.gender || rep.gender} schoolName={sg.school_name} />
      <h2 className="text-3xl font-extrabold text-[#8A6D2B] my-4">{honor ? "شهادة تكريم" : "شهادة شكر وتقدير"}</h2>
      <p className="text-lg">تتقدم إدارة المدرسة والإرشاد الطلابي {girl ? "للطالبة" : "للطالب"}</p>
      <p className="text-3xl font-bold text-[#3E6A56] my-4">{s.name}</p>
      <p className="text-lg">{honor
        ? `تكريمًا لاستمرار ${girl ? "تحسّنها" : "تحسّنه"} في الانضباط والسلوك لمدة شهر كامل`
        : `تقديرًا ${girl ? "لتحسّنها" : "لتحسّنه"} الملحوظ في الانضباط والسلوك خلال أسبوع ${fmtWeek(rep.week)}`}</p>
      <p className="mt-2">متمنين {girl ? "لها" : "له"} دوام التوفيق والتميز</p>
      <div className="grid grid-cols-2 text-lg mt-12 gap-4">
        <span>الموجه الطلابي<br />{sg.counselor}</span>
        <span>مدير المدرسة<br />{sg.principal}</span>
      </div>
    </div>
  );
}

export const printReport = () => {
  document.body.classList.add("printing-report");
  setTimeout(() => {
    window.print();
    setTimeout(() => document.body.classList.remove("printing-report"), 500);
  }, 50);
};
